import { ArrowUp, CornerDownLeft, Sparkles } from 'lucide-react'
import { useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react'
import { createPortal } from 'react-dom'
import { useLocation } from 'wouter'
import type { AskSubject } from '../lib/types'
import { useAgent } from './Shell'

/**
 * Ask about anything on screen. Two ways in, and neither takes a normal click:
 *  - hover a card and a small ✦ appears on its top edge; click it to ask about the whole object
 *  - hold ⌥ and the row, cell or card under the pointer is outlined; ⌥-click asks about just that
 * Either opens a composer anchored to the object, which hands the question and a snapshot of what's
 * on screen to the agent. Any card in the main pane is askable; data-ask-* attributes refine one and
 * data-ask="off" opts it out.
 */

const ASKABLE = '.card, .kpi-card'
const KEEP_OUT = '.topbar, .menu, .cmdk-bg, .ask-composer, .ask-spark, [data-ask="off"]'
const SNAPSHOT_MAX = 6000

type Target = { el: Element; box: Element; subject: AskSubject; label: string }

/**
 * The words in an element as written: hidden parts (the short labels a narrow screen swaps in) are
 * skipped, elements are spaced apart, and text-transform is ignored (innerText would read styled
 * headers as UPPERCASE).
 */
function text(el: Element | null | undefined): string {
  if (!el) return ''
  const parts: string[] = []
  const walk = (n: Node) => {
    if (n.nodeType === Node.TEXT_NODE) parts.push(n.textContent ?? '')
    else if (n instanceof Element) {
      if (n.checkVisibility && !n.checkVisibility()) return
      if (n instanceof SVGElement) return
      parts.push(' ')
      for (const c of n.childNodes) walk(c)
      parts.push(' ')
    }
  }
  walk(el)
  return parts.join('').replace(/\s+/g, ' ').trim()
}
const shown = (el: Element) => ((el as HTMLElement).innerText ?? el.textContent ?? '').replace(/\s+/g, ' ').trim()

/** The outermost askable object under a node in the main pane, if any. */
function objectAt(node: EventTarget | null): Element | null {
  if (!(node instanceof Element) || !node.closest('.content') || node.closest(KEEP_OUT)) return null
  let found: Element | null = null
  for (let el: Element | null = node.closest(ASKABLE); el; el = el.parentElement?.closest(ASKABLE) ?? null) found = el
  return found
}

function titleOf(el: Element): string {
  const h = el as HTMLElement
  return (
    h.dataset.askTitle ||
    text(el.querySelector('.card-head h3')) ||
    text(el.querySelector('.stat-label')) ||
    text(el.querySelector('h1, h2, h3')) ||
    text(el).slice(0, 60)
  )
}

function kindOf(el: Element): string {
  const h = el as HTMLElement
  if (h.dataset.askKind) return h.dataset.askKind
  if (el.matches('.kpi-card, .stat')) return 'KPI'
  if (el.querySelector('table')) return 'table'
  if (el.querySelector('svg')) return 'chart'
  return 'panel'
}

/** What's on screen, compactly: the header line, then each table as rows of cells. */
function snapshotOf(el: Element): string {
  const lines: string[] = []
  const head = el.querySelector('.card-head')
  if (head) lines.push([text(head.querySelector('h3')), text(head.querySelector('.card-sub'))].filter(Boolean).join(' — '))
  const tables = el.querySelectorAll('table')
  if (tables.length)
    for (const t of tables)
      for (const r of t.rows) {
        const cells = [...r.cells].map(c => text(c)).filter(Boolean)
        if (cells.length) lines.push(cells.join(' | '))
      }
  else lines.push(shown(el))
  const out = lines.join('\n')
  return out.length > SNAPSHOT_MAX ? `${out.slice(0, SNAPSHOT_MAX)}\n…` : out
}

/** The header text over a cell, counting column spans. */
/** A table's column headers: its thead, or a first row made of <th>s. Lists (like the review list) have none. */
function headerRow(table: HTMLTableElement | null): HTMLTableRowElement | undefined {
  if (!table) return undefined
  if (table.tHead?.rows.length) return table.tHead.rows[table.tHead.rows.length - 1]
  const first = table.rows[0]
  return first && [...first.cells].every(c => c.tagName === 'TH') ? first : undefined
}

/** What a row is called: its first cell with words in it, and within that the first line (a title, not its byline). */
function rowName(row: HTMLTableRowElement): string {
  const cell = [...row.cells].find(c => text(c))
  if (!cell) return ''
  const first = cell.querySelector('.strong, b, strong') ?? cell.firstElementChild
  const name = (first && text(first)) || text(cell)
  return name.length > 90 ? `${name.slice(0, 88)}…` : name
}

function headerFor(cell: HTMLTableCellElement): string {
  const head = headerRow(cell.closest('table'))
  if (!head || head === cell.parentElement) return ''
  let col = 0
  for (const c of (cell.parentElement as HTMLTableRowElement).cells) {
    if (c === cell) break
    col += c.colSpan
  }
  let at = 0
  for (const h of head.cells) {
    if (col < at + h.colSpan) return text(h)
    at += h.colSpan
  }
  return ''
}

function subjectFor(object: Element, focus?: AskSubject['focus']): AskSubject {
  const hint = (object as HTMLElement).dataset.askHint
  const page = text(document.querySelector('.crumbs b'))
  return {
    kind: kindOf(object),
    title: titleOf(object),
    focus,
    snapshot: snapshotOf(object),
    hint: [hint, page && `shown on the ${page} page (${window.location.pathname.replace(/^\/w\/[^/]+/, '') || '/'})`]
      .filter(Boolean)
      .join('; '),
  }
}

const labelOf = (s: AskSubject) => [s.title, s.focus?.row, s.focus?.col].filter(Boolean).join(' · ')

/**
 * Where ⌥ points: a data cell (its row and column), a row (its first cell), a column header, a
 * smaller askable inside the object, or the object itself.
 */
function pointAt(node: EventTarget | null): Target | null {
  const object = objectAt(node)
  if (!object || !(node instanceof Element)) return null
  const cell = node.closest('td, th') as HTMLTableCellElement | null
  if (cell && object.contains(cell)) {
    const row = cell.parentElement as HTMLTableRowElement
    const inHead = !!cell.closest('thead')
    if (inHead) {
      const subject = subjectFor(object, { col: text(cell) })
      return { el: object, box: cell, subject, label: labelOf(subject) }
    }
    const rowLabel = rowName(row)
    // a list without column headers is pointed at a row at a time; so is a table's label column
    if (cell.cellIndex === 0 || !headerRow(cell.closest('table'))) {
      const subject = subjectFor(object, { row: rowLabel })
      return { el: object, box: row, subject, label: labelOf(subject) }
    }
    const col = headerFor(cell)
    const value = text(cell)
    const subject = subjectFor(object, { row: rowLabel || undefined, col: col || undefined, value: value || undefined })
    return { el: object, box: cell, subject, label: labelOf(subject) }
  }
  const inner = node.closest(ASKABLE)
  if (inner && inner !== object) {
    const subject = { ...subjectFor(inner), hint: `inside "${titleOf(object)}"` }
    return { el: inner, box: inner, subject, label: labelOf(subject) }
  }
  const subject = subjectFor(object)
  return { el: object, box: object, subject, label: labelOf(subject) }
}

/** Three good first questions for what was pointed at. */
function suggestions(s: AskSubject): string[] {
  const { row, col, value } = s.focus ?? {}
  if (row && col) {
    // "headroom" reads as a word in a sentence; "FY27 opex target" and "Oct 2026" keep their capitals
    const c = /^[A-Z][a-z]/.test(col) ? col.charAt(0).toLowerCase() + col.slice(1) : col
    const when = /^[A-Z][a-z]{2} \d{4}$|^FY\d|Q\d/.test(col)
    return when
      ? [
          `Why is ${row} ${value ?? 'where it is'} in ${col}?`,
          `What's driving ${row} in ${col}?`,
          `How does ${col} compare with the periods around it?`,
        ]
      : [`Why is ${row}'s ${c} ${value ?? 'where it is'}?`, `What's driving ${row}'s ${c}?`, 'What would move it the most?']
  }
  if (row) return [`What's driving ${row}?`, `How does ${row} compare with the others?`, `What's the risk in ${row}?`]
  if (col) return [`Explain ${col}.`, `Which row stands out on ${col}?`, `How is ${col} calculated?`]
  switch (s.kind) {
    case 'KPI':
      return [`What's driving ${s.title}?`, `How has ${s.title} moved against the budget?`, 'What would move it the most?']
    case 'chart':
      return ['What explains the shape of this?', 'Where does it turn, and why?', 'What should I watch here?']
    case 'table':
      return ['What stands out here?', 'Where is the risk?', 'How are these numbers built?']
    default:
      return ['Explain this.', 'What should I do about it?', 'What changed recently?']
  }
}

type Rect = { top: number; left: number; width: number; height: number }
const rectOf = (el: Element): Rect => {
  const r = el.getBoundingClientRect()
  return { top: r.top, left: r.left, width: r.width, height: r.height }
}

export function AskLayer() {
  const { ask } = useAgent()
  const [location] = useLocation()
  const [hover, setHover] = useState<{ el: Element; rect: Rect } | null>(null)
  const [pointing, setPointing] = useState<(Target & { rect: Rect }) | null>(null)
  const [open, setOpen] = useState<(Target & { rect: Rect }) | null>(null)
  const alt = useRef(false)
  const last = useRef<EventTarget | null>(null)
  const showTimer = useRef<number | undefined>(undefined)
  const hideTimer = useRef<number | undefined>(undefined)
  const hoverEl = useRef<Element | null>(null)
  const openRef = useRef(open)
  openRef.current = open

  const clearTimers = useCallback(() => {
    window.clearTimeout(showTimer.current)
    window.clearTimeout(hideTimer.current)
  }, [])

  const refreshPointing = useCallback(() => {
    if (!alt.current || openRef.current) return setPointing(null)
    const t = pointAt(last.current)
    setPointing(t ? { ...t, rect: rectOf(t.box) } : null)
  }, [])

  // biome-ignore lint/correctness/useExhaustiveDependencies: a new page closes whatever was open
  useEffect(() => {
    setOpen(null)
    hoverEl.current = null
    setHover(null)
    setPointing(null)
  }, [location])

  useEffect(() => {
    const onMove = (e: PointerEvent) => {
      if (e.pointerType === 'touch') return
      last.current = e.target
      alt.current = e.altKey
      document.body.classList.toggle('ask-pointing', e.altKey && !openRef.current)
      if (e.altKey) {
        clearTimers()
        hoverEl.current = null
        setHover(null)
        refreshPointing()
        return
      }
      setPointing(null)
      // the spark sits on the object's edge, so moving onto it keeps the object hovered
      if ((e.target as Element | null)?.closest?.('.ask-spark')) return window.clearTimeout(hideTimer.current)
      const object = objectAt(e.target)
      if (object === hoverEl.current) return window.clearTimeout(hideTimer.current)
      clearTimers()
      if (!object) {
        if (hoverEl.current)
          hideTimer.current = window.setTimeout(() => {
            hoverEl.current = null
            setHover(null)
          }, 180)
        return
      }
      // a short dwell, so sweeping the pointer across the page doesn't light everything up
      showTimer.current = window.setTimeout(
        () => {
          hoverEl.current = object
          setHover({ el: object, rect: rectOf(object) })
        },
        hoverEl.current ? 60 : 260,
      )
    }
    const typing = (el: EventTarget | null) => el instanceof Element && !!el.closest('input, textarea, select, [contenteditable="true"]')
    const onKey = (e: KeyboardEvent) => {
      if (e.key !== 'Alt') return
      // ⌥ types characters (™, é) in a text field: that's typing, not pointing
      if (e.type === 'keydown' && typing(document.activeElement)) return
      // a bare Alt release opens the browser's menu on Windows; not after it was used to point
      if (e.type === 'keyup' && alt.current && last.current && pointAt(last.current)) e.preventDefault()
      alt.current = e.type === 'keydown'
      document.body.classList.toggle('ask-pointing', alt.current && !openRef.current)
      if (alt.current) {
        hoverEl.current = null
        setHover(null)
      }
      refreshPointing()
    }
    const onBlur = () => {
      alt.current = false
      document.body.classList.remove('ask-pointing')
      setPointing(null)
    }
    // ⌥ is how you point, so while it's held a press in the main pane is ours: no text selection, no link download
    const onDown = (e: MouseEvent) => {
      if (e.altKey && e.button === 0 && !typing(e.target) && pointAt(e.target)) e.preventDefault()
    }
    const onClick = (e: MouseEvent) => {
      if (!e.altKey || e.button !== 0 || typing(e.target)) return
      const t = pointAt(e.target)
      if (!t) return
      e.preventDefault()
      e.stopPropagation()
      document.body.classList.remove('ask-pointing')
      setPointing(null)
      setOpen({ ...t, rect: rectOf(t.box) })
    }
    const onScroll = () => {
      setHover(h => (h ? { ...h, rect: rectOf(h.el) } : h))
      setOpen(o => (o ? { ...o, rect: rectOf(o.box) } : o))
      refreshPointing()
    }
    window.addEventListener('pointermove', onMove, { passive: true })
    window.addEventListener('keydown', onKey)
    window.addEventListener('keyup', onKey)
    window.addEventListener('blur', onBlur)
    window.addEventListener('mousedown', onDown, true)
    window.addEventListener('click', onClick, true)
    window.addEventListener('scroll', onScroll, true)
    window.addEventListener('resize', onScroll)
    return () => {
      clearTimers()
      document.body.classList.remove('ask-pointing')
      window.removeEventListener('pointermove', onMove)
      window.removeEventListener('keydown', onKey)
      window.removeEventListener('keyup', onKey)
      window.removeEventListener('blur', onBlur)
      window.removeEventListener('mousedown', onDown, true)
      window.removeEventListener('click', onClick, true)
      window.removeEventListener('scroll', onScroll, true)
      window.removeEventListener('resize', onScroll)
    }
  }, [refreshPointing, clearTimers])

  const openFor = (el: Element) => {
    const subject = subjectFor(el)
    hoverEl.current = null
    setHover(null)
    setOpen({ el, box: el, subject, label: labelOf(subject), rect: rectOf(el) })
  }

  const send = (prompt: string) => {
    if (!open || !prompt.trim()) return
    ask(prompt.trim(), open.label, open.subject)
    setOpen(null)
  }

  return createPortal(
    <>
      {hover && !open && !pointing && (
        <button
          type="button"
          className="ask-spark"
          style={{ top: Math.max(hover.rect.top - 11, 52), left: hover.rect.left + hover.rect.width - 34 }}
          onClick={() => openFor(hover.el)}
          title="Ask about this · hold ⌥ to point at a row or cell"
          aria-label={`Ask about ${titleOf(hover.el)}`}
        >
          <Sparkles size={12} />
        </button>
      )}
      {pointing && (
        <>
          <div className="ask-outline" style={box(pointing.rect)} />
          <div className="ask-tag" style={{ top: Math.max(pointing.rect.top - 24, 4), left: pointing.rect.left }}>
            <Sparkles size={11} /> {pointing.label}
          </div>
        </>
      )}
      {open && (
        <>
          <div className="ask-outline held" style={box(open.rect)} />
          <Composer target={open} onSend={send} onClose={() => setOpen(null)} />
        </>
      )}
    </>,
    document.body,
  )
}

const box = (r: Rect) => ({ top: r.top - 2, left: r.left - 2, width: r.width + 4, height: r.height + 4 })

function Composer({ target, onSend, onClose }: { target: Target & { rect: Rect }; onSend: (q: string) => void; onClose: () => void }) {
  const [q, setQ] = useState('')
  const [pick, setPick] = useState(-1)
  const ref = useRef<HTMLDivElement>(null)
  const input = useRef<HTMLInputElement>(null)
  const ideas = suggestions(target.subject)
  const [pos, setPos] = useState<{ top: number; left: number }>({ top: -9999, left: -9999 })

  // below the object when there's room, above it when not, always inside the window
  useLayoutEffect(() => {
    const h = ref.current?.offsetHeight ?? 220
    const w = ref.current?.offsetWidth ?? 400
    const r = target.rect
    const below = r.top + r.height + 8
    const top = below + h < window.innerHeight - 8 ? below : Math.max(8, r.top - h - 8)
    // a whole card is big: open near the pointer's corner (the spark) rather than under its far edge
    const anchorTop = r.height > 260 ? Math.min(Math.max(r.top + 16, 60), window.innerHeight - h - 8) : top
    const left = Math.min(Math.max(8, r.height > 260 ? r.left + r.width - w - 12 : r.left), window.innerWidth - w - 8)
    setPos({ top: anchorTop, left })
  }, [target.rect])

  useEffect(() => {
    input.current?.focus()
    const away = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) onClose()
    }
    window.addEventListener('mousedown', away)
    return () => window.removeEventListener('mousedown', away)
  }, [onClose])

  const focus = target.subject.focus
  return (
    <div
      ref={ref}
      className="ask-composer"
      style={pos}
      role="dialog"
      aria-label={`Ask about ${target.label}`}
      onKeyDown={e => {
        if (e.key === 'Escape') {
          e.preventDefault()
          onClose()
        } else if (e.key === 'ArrowDown' || e.key === 'ArrowUp') {
          e.preventDefault()
          setPick(p => {
            const n = ideas.length
            return e.key === 'ArrowDown' ? (p + 1 >= n ? -1 : p + 1) : p <= -1 ? n - 1 : p - 1
          })
        }
      }}
    >
      <div className="ask-about">
        <Sparkles size={12} />
        <span className="ellipsis">
          <span className="faint">Ask about </span>
          {target.label}
        </span>
        {focus?.value && <span className="ask-value num">{focus.value}</span>}
      </div>
      <form
        className="ask-input"
        onSubmit={e => {
          e.preventDefault()
          onSend(pick >= 0 && !q.trim() ? (ideas[pick] ?? '') : q)
        }}
      >
        <input
          ref={input}
          value={q}
          onChange={e => {
            setQ(e.target.value)
            setPick(-1)
          }}
          placeholder="Ask anything about this…"
          aria-label="Your question"
        />
        <button type="submit" className="ask-go" disabled={!q.trim() && pick < 0} aria-label="Ask">
          <ArrowUp size={13} />
        </button>
      </form>
      <div className="ask-ideas">
        {ideas.map((s, i) => (
          <button
            type="button"
            key={s}
            className={`ask-idea ${pick === i ? 'on' : ''}`}
            onMouseEnter={() => setPick(i)}
            onMouseLeave={() => setPick(-1)}
            onClick={() => onSend(s)}
          >
            <span className="grow">{s}</span>
            {pick === i && <CornerDownLeft size={11} className="faint" />}
          </button>
        ))}
      </div>
      <div className="ask-foot">
        {focus ? (
          <span>The agent sees this card and where you pointed.</span>
        ) : (
          <span>
            Hold <kbd>⌥</kbd> and click a row or cell to ask about just that.
          </span>
        )}
      </div>
    </div>
  )
}
